import { useState } from "react";
import { useSeo } from "@/lib/seo";
import { IMAGES } from "@/data/site";
import PageHero from "@/components/PageHero";
import { Reveal, Overline } from "@/components/Reveal";
import LeadForm from "@/components/LeadForm";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Lock, ShieldCheck, FileLock2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";

export default function DataRoom() {
  useSeo("Investment & Development Partners — Data Room | 12 Stone",
    "Select 12 Stone project information is available to qualified investment and development partners after confidentiality documentation. Request project access, request an NDA, or sign in to the data room.");
  const [modal, setModal] = useState(null); // 'access' | 'nda' | 'login'
  const [code, setCode] = useState("");

  const contractor = (
    <div className="rounded-xl border border-border bg-card p-7">
      <div className="flex items-center gap-3 mb-3"><HardHatIcon /><h3 className="font-display text-lg font-semibold text-foreground">12 Stone Contractor Network</h3></div>
      <p className="text-sm text-muted-foreground">Contractors supporting data center and infrastructure projects may apply to become part of the 12 Stone qualified contractor network. Prequalification connects later to our contractor-vetting application.</p>
      <Button onClick={() => setModal("contractor")} className="mt-5 bg-transparent hover:bg-secondary text-primary border border-border rounded-full" data-testid="contractor-prequal-btn">CONTRACTOR PREQUALIFICATION</Button>
    </div>
  );

  return (
    <div>
      <PageHero overline="Investment & Development Partners" title="Select Access, After Qualification"
        subtitle="Certain project information is available only after qualification and confidentiality documentation. We do not expose confidential project materials publicly."
        image={IMAGES.dataCenter2} />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { icon: FileLock2, title: "Request Project Access", desc: "Qualified investors and development partners can request access to project-level materials.", btn: "REQUEST PROJECT ACCESS", key: "access", test: "request-project-access" },
            { icon: ShieldCheck, title: "Request NDA", desc: "Begin confidentiality documentation to review protected information.", btn: "REQUEST NDA", key: "nda", test: "request-nda" },
            { icon: KeyRound, title: "Data Room Login", desc: "Existing partners can enter a data room access code.", btn: "DATA ROOM LOGIN", key: "login", test: "data-room-login" },
          ].map((c, i) => (
            <Reveal key={c.key} delay={i * 0.06}>
              <div className="rounded-xl border border-border bg-card p-7 h-full flex flex-col">
                <div className="h-11 w-11 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary mb-4"><c.icon className="h-5 w-5" /></div>
                <h3 className="font-display text-lg font-semibold text-foreground">{c.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground flex-1">{c.desc}</p>
                <Button onClick={() => setModal(c.key)} data-testid={`${c.test}-btn`} className="mt-5 bg-primary hover:bg-primary/90 text-white font-semibold rounded-full">{c.btn}</Button>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-16"><Reveal><Overline>Contractor Vetting</Overline><div className="mt-6">{contractor}</div></Reveal></div>
      </section>

      {/* Access / NDA */}
      <Dialog open={modal === "access" || modal === "nda" || modal === "contractor"} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-foreground">
            {modal === "access" ? "Request Project Access" : modal === "nda" ? "Request NDA" : "Contractor Prequalification"}
          </DialogTitle></DialogHeader>
          <LeadForm
            leadType={modal === "contractor" ? "contractor" : "partner"}
            defaultCategory={modal === "access" ? "Project Access Request" : modal === "nda" ? "NDA Request" : "Contractor Prequalification"}
            submitLabel="SUBMIT REQUEST" testId="dataroom-request" />
        </DialogContent>
      </Dialog>

      {/* Login */}
      <Dialog open={modal === "login"} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader><DialogTitle className="font-display text-foreground flex items-center gap-2"><Lock className="h-5 w-5 text-primary" /> Data Room Login</DialogTitle></DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); toast.error("Access code not recognized. Please request project access."); }} className="space-y-4">
            <Input placeholder="Access code" value={code} onChange={(e) => setCode(e.target.value)} data-testid="data-room-code" className="bg-secondary/50 border-border" />
            <Button type="submit" data-testid="data-room-unlock-btn" className="w-full bg-primary text-white rounded-full">UNLOCK DATA ROOM</Button>
            <p className="text-xs text-muted-foreground text-center">The secure data room connects to a future gated portal. Qualified partners will receive credentials after NDA.</p>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function HardHatIcon() {
  return <div className="h-11 w-11 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary"><ShieldCheck className="h-5 w-5" /></div>;
}
